import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import PropTypes from "prop-types";

import {
  Button,
  Divider,
  Form,
  Input,
  Radio,
  message,
  Spin,
  Tag,
  Row,
  Col,
  DatePicker,
  Space,
  Select,
  Table,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  rowLayout,
  colLayout,
  EntityStatus,
  tailItemLayout,
  ServiceOfferingActions,
  ServiceOfferingScopeRestriction,
} from "../../Constants/Constants";
import { convertServiceInstanceList } from "../../Helpers/ServiceInstanceHelpers";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { apiCallback } from "../../ApiHandlers/Common";
import { onFormSubmit } from "../../ApiHandlers/ServiceOfferingApiHandlers";
import { convertServiceList } from "../../Helpers/ServiceHelpers";
import {
  defaultSort,
  selectFilterFunction,
  selectAdvanceFilterFunction,
} from "../../Constants/Utilities";
import { convertServiceOfferingList } from "../../Helpers/ServiceOfferingHelpers";
import Loading from "../Common/Loading";
import { DiscardWarning } from "../Common/DiscardWarning";
import { BreadCrumbs } from "../Common/BreadCrumbs";

const getInitialValues = (record, offerRecord) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
    scoperestriction: ServiceOfferingScopeRestriction.labTrial.value,
  };
  if (record) {
    initialValues.offeringname = record.offeringname;
    initialValues.servicename = record.offeringname;
    initialValues.instancename = record.instancename;
    initialValues.status = record.status;
    if (offerRecord && offerRecord.instanceData) {
      const { licenseinfo } = offerRecord.instanceData[0];
      initialValues.scoperestriction =
        licenseinfo?.scoperestriction ||
        ServiceOfferingScopeRestriction.labTrial.value;
      initialValues.volumerestriction = licenseinfo?.volumerestriction;
      initialValues.timeduration = licenseinfo?.timerestriction?.duration;
      initialValues.timeactivation = licenseinfo?.timerestriction
        ?.activationdate
        ? moment(licenseinfo.timerestriction.activationdate)
        : null;
    }
  }
  return initialValues;
};

const getTitle = (action) => {
  switch (action) {
    case ServiceOfferingActions.add:
      return "Add";
    case ServiceOfferingActions.edit:
      return "Update";
    case ServiceOfferingActions.editInstance:
      return "Update";
    case ServiceOfferingActions.view:
      return "View";
    default:
      return "";
  }
};

const ServiceOfferingForm = (props) => {
  console.log("ServiceOfferingFormForm props", props);
  const {
    action,
    history,
    record,
    serviceApi,
    serviceInstanceApi,
    onActionClick,
    offerRecord,
  } = props;

  const title = useMemo(() => getTitle(action), []);
  const [initialValues, setInitialValues] = useState(
    getInitialValues(record, offerRecord)
  );

  // service info
  const [serviceDataLoading, setServiceDataLoading] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  const [form] = Form.useForm();
  const [processing, setProcessing] = useState(false);
  // record and error info
  const [offeringRecordFetching, setofferingRecordFetching] = useState(false);
  const [fetchError, setfetchError] = useState(false);
  //instance info
  const [instance, setInstance] = useState({ data: [], loading: false });
  const [instanceData, setInstanceData] = useState([]);
  const [selectedInstances, setSelectedInstances] = useState([]);

  const isAddOfferForm = action === ServiceOfferingActions.add;
  const isViewForm = action === ServiceOfferingActions.view;

  const isOfferForm = isAddOfferForm || action === ServiceOfferingActions.edit;
  const isInstForm =
    action === ServiceOfferingActions.viewInstance ||
    action === ServiceOfferingActions.editInstance;

  useEffect(() => {
    (action === ServiceOfferingActions.add ||
      action === ServiceOfferingActions.edit) &&
      fetchServiceData();

    (action === ServiceOfferingActions.viewInstance ||
      action === ServiceOfferingActions.editInstance) &&
      fetchOfferingRecord();
  }, []);

  useEffect(() => {
    form.setFieldsValue({ instanceTable: instanceData });
  }, [instanceData]);

  const columns = [
    {
      title: "Service Name",
      dataIndex: "servicename",
      sorter: (a, b) => defaultSort(a.servicename, b.servicename),
      defaultSortOrder: "ascend",
    },
    {
      title: "Service Instance",
      dataIndex: "instancename",
      sorter: (a, b) => defaultSort(a.instancename, b.instancename),
    },
    {
      title: "",
      align: "center",
      width: "10%",
      render: (_, record) => (
        <div className="entity-list-actions">
          <DeleteOutlined onClick={onInstanceRemove(record)} />
        </div>
      ),
    },
  ];

  const fetchServiceData = () => {
    console.log("fetchserviceList...");
    setServiceDataLoading(true);

    const errorCallback = (error) => {
      setServiceDataLoading(false);
      message.error("Error fetching service list");
    };
    const successCallback = (data) => {
      const serviceData = convertServiceList(data);
      console.log("fetchServiceData data", serviceData);
      setServiceDataLoading(false);
      setServiceData(serviceData);
    };

    try {
      serviceApi.getService(
        apiCallback({
          history,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const fetchOfferingRecord = (record, history, serviceOfferingApi) => {
    const errorCallback = (error) => {
      message.error("Error fetching Service Offering record");
      setofferingRecordFetching(false);
      setfetchError(true);
    };
    try {
      console.log("fetchOfferingRecord...");
      if (
        !record ||
        !record.offerid ||
        !record.serviceid ||
        !record.instanceid
      ) {
        return;
      }
      setofferingRecordFetching(true);
      serviceOfferingApi.getServiceOfferingByInstance(
        record.offerid,
        record.serviceid,
        record.instanceid,
        apiCallback({
          history,
          errorCallback,
          successCallback: (data) => {
            const formattedData = convertServiceOfferingList(data);
            const initialValues = this.getInitialValues(formattedData[0]);
            setInitialValues(initialValues);
            setofferingRecordFetching(false);
          },
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const fetchServiceInstanceData = (serviceId) => {
    console.log("fetchServiceInstanceData", serviceId);
    setInstance({ loading: true });
    const errorCallback = () => {
      message.error("Error fetching Service Instance list");
      setInstance({ loading: false, data: [] });
    };
    try {
      serviceInstanceApi.getServiceInstanceByServiceId(
        serviceId,
        apiCallback({
          history,
          errorCallback,
          successCallback: (data) => {
            const instanceData = convertServiceInstanceList(data);
            setInstance({ loading: false, data: instanceData });
          },
        })
      );
    } catch (error) {
      errorCallback();
    }
  };

  const onUpdateList = () => {
    if (
      !selectedService ||
      !selectedInstances ||
      selectedInstances.length === 0
    ) {
      return;
    }
    const instData = [...instanceData];
    const { id, name } = selectedService;
    const instList = instance.data?.filter((item) =>
      selectedInstances.includes(item.key + "")
    );
    instList.forEach((item) => {
      const itemExist = instData.some(
        (i) => i.key === id + ":" + item.instanceId
      );
      if (!itemExist) {
        instData.push({
          key: id + ":" + item.instanceId,
          servicename: name,
          instancename: item.name,
          serviceid: id,
          instanceid: item.instanceId,
        });
      }
    });
    console.log("onUpdateList instData", instData);
    setInstanceData(instData);
    setSelectedInstances([]);
  };

  const onServiceChange = (_, option) => {
    const serviceId = option?.key;
    if (!serviceId) {
      setSelectedService(null);
      setSelectedInstances([]);
    } else if (serviceId !== selectedService?.id) {
      setSelectedService({ id: serviceId, name: option.value });
      fetchServiceInstanceData(serviceId);
      setSelectedInstances([]);
    }
  };

  const onInstanceRemove = (record) => () => {
    const instData = instanceData.filter(
      (item) =>
        !(
          item.serviceid === record.serviceid &&
          item.instanceid === record.instanceid
        )
    );
    setInstanceData(instData);
  };

  const onFormReset = () => {
    form.resetFields();
    setInstanceData([]);
    setSelectedService(null);
    setSelectedInstances([]);
  };

  const displayOfferingName = (disabled) => (
    <Form.Item
      name="offeringname"
      label="Offering Name:"
      rules={[{ required: true, message: "Please fill in this field" }]}
    >
      <Input
        title="Name should be unique"
        style={{ maxWidth: "300px" }}
        disabled={disabled}
      />
    </Form.Item>
  );

  const displayInstanceInfo = (
    <Row {...rowLayout}>
      <Col {...colLayout}>
        <Form.Item
          name="servicename"
          label="Service Name:"
          rules={[{ required: true }]}
        >
          <Input disabled style={{ maxWidth: "300px" }} />
        </Form.Item>
      </Col>
      <Col {...colLayout}>
        <Form.Item
          name="instancename"
          label="Instance Name:"
          rules={[{ required: true }]}
        >
          <Input disabled style={{ maxWidth: "300px" }} />
        </Form.Item>
      </Col>
    </Row>
  );

  const displayStatus = (status) => (
    <Form.Item name="status" label="Status:" rules={[{ required: true }]}>
      <Tag color={status === EntityStatus.ACTIVE ? "green" : "red"}>
        {status}
      </Tag>
    </Form.Item>
  );

  const displayServiceList = ({
    serviceData,
    serviceDataLoading,
    selectedService,
    onServiceChange,
  }) => (
    <Form.Item name="service" label="Service:">
      <Select
        showSearch
        allowClear
        className="tagged-dropdown"
        loading={serviceDataLoading}
        style={{ width: "90%", maxWidth: "300px" }}
        disabled={serviceDataLoading}
        filterOption={selectAdvanceFilterFunction("value")}
        value={selectedService?.id}
        placeholder={
          serviceDataLoading ? "Loading Service list..." : "Select Service"
        }
        onChange={onServiceChange}
      >
        {serviceData?.map((item) => (
          <Select.Option
            key={item.serviceId}
            value={item.name}
            title={item.name}
            className="tagged-dropdown-options"
          >
            {item.name}
            <Tag color={item.status === EntityStatus.ACTIVE ? "green" : "red"}>
              {item.status}
            </Tag>
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  const displayInstanceList = ({
    instance,
    selectedService,
    selectedInstances,
    setSelectedInstances,
    onUpdateList,
  }) => (
    <Form.Item name="instances" label="Service Instance:">
      <div className="display-flex-column">
        <Select
          allowClear
          mode="multiple"
          loading={instance.loading}
          value={selectedInstances}
          className="offering-instance-list custom-scrollbar tagged-dropdown"
          disabled={instance.loading || !selectedService}
          placeholder={
            !selectedService
              ? "Select Service to view Instance list"
              : instance.loading
              ? "Loading Service Instances..."
              : "Select Service Instance(s)"
          }
          filterOption={selectFilterFunction}
          onChange={(value) => setSelectedInstances(value)}
        >
          {instance.data?.map((item) => (
            <Select.Option
              key={item.instanceId}
              title={item.name}
              className="tagged-dropdown-options"
              disabled={item.status === EntityStatus.INACTIVE}
            >
              {item.name}
              <Tag
                color={item.status === EntityStatus.ACTIVE ? "green" : "red"}
              >
                {item.status}
              </Tag>
            </Select.Option>
          ))}
        </Select>
        <Button
          type="primary"
          className="add-offering-instance"
          onClick={onUpdateList}
        >
          Update List
        </Button>
      </div>
    </Form.Item>
  );

  const displayInstanceTable = (columns, instanceData) => (
    <Form.Item
      name="instanceTable"
      label="Instance List:"
      rules={[
        {
          required: true,
          message: "Please add 1 or more Service Instance",
        },
      ]}
    >
      <Table
        className="offering-instance-table custom-scrollbar"
        size="small"
        bordered={true}
        columns={columns}
        dataSource={instanceData}
        pagination={false}
        scroll={{ x: 240, y: 130 }}
      ></Table>
    </Form.Item>
  );

  const displayLicenceInfo = (initialValues) => (
    <fieldset>
      <legend>License Info:</legend>
      <Row {...rowLayout}>
        <Col {...colLayout}>
          <Form.Item
            label="Scope:"
            name="scoperestriction"
            className="ant-form-row-layout"
            rules={[{ required: true }]}
            style={{ alignItems: "baseline" }}
          >
            <Radio.Group value={initialValues.scoperestriction}>
              <Space direction="vertical">
                {Object.values(ServiceOfferingScopeRestriction).map((item) => (
                  <Radio key={item.value} value={item.value}>
                    {item.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col {...colLayout}>
          <Form.Item name="volumerestriction" label="Volume:">
            <Input
              type="number"
              min="0"
              step="1"
              style={{ maxWidth: "100px" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row {...rowLayout}>
        <Col {...colLayout}>
          <Form.Item name="timeactivation" label="Activation Date:">
            <DatePicker
              placeholder="Select date-time"
              format="DD-MM-YYYY HH:mm:ss"
              disabled={isViewForm}
              showTime={{ defaultValue: moment("00:00:00", "HH:mm:ss") }}
            />
          </Form.Item>
        </Col>
        <Col {...colLayout}>
          <Form.Item name="timeduration" label="Time Duration:">
            <Input
              type="number"
              min="0"
              step="1"
              style={{ maxWidth: "100px" }}
            />
          </Form.Item>
        </Col>
      </Row>
    </fieldset>
  );

  const displaySubmitBottons = (title, onFormReset) => (
    <Form.Item {...tailItemLayout}>
      <Button type="primary" htmlType="submit" className="submit-button">
        {title}
      </Button>
      <Button htmlType="button" onClick={onFormReset} className="cancel-button">
        Cancel
      </Button>
    </Form.Item>
  );

  const onBackClick = () => {
    const isChanged =
      action === ServiceOfferingActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "offeringname",
      "servicename",
      "instancename",
      "status",
      "service",
      "scoperestriction",
      "volumerestriction",
      "timeactivation",
      "timeduration",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };
  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Service Offering`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        {offeringRecordFetching ? (
          <Loading height="300px" message="Fetching details..." />
        ) : (
          <fieldset disabled={isViewForm}>
            <Form
              form={form}
              name="service-offering-form"
              layout="vertical"
              onFinish={onFormSubmit({
                props,
                setProcessing,
                setInitialValues,
              })}
              initialValues={initialValues}
              requiredMark={"optional"}
              size={"middle"}
            >
              <Spin spinning={processing} tip={"Processing, please wait..."}>
                <Row {...rowLayout}>
                  <Col {...colLayout}>
                    {displayOfferingName(!isAddOfferForm)}
                  </Col>
                  {(isAddOfferForm || isInstForm) && (
                    <Col {...colLayout}>
                      {displayStatus(initialValues.status)}
                    </Col>
                  )}
                </Row>

                {isInstForm && displayInstanceInfo}

                {isOfferForm && (
                  <Row {...rowLayout}>
                    <Col {...colLayout}>
                      {displayServiceList({
                        serviceData,
                        serviceDataLoading,
                        selectedService,
                        onServiceChange,
                      })}
                      {displayInstanceList({
                        instance,
                        selectedService,
                        selectedInstances,
                        setSelectedInstances,
                        onUpdateList,
                      })}
                    </Col>
                    <Col {...colLayout}>
                      {displayInstanceTable(columns, instanceData)}
                    </Col>
                  </Row>
                )}

                {displayLicenceInfo(initialValues)}

                {!isViewForm &&
                  displaySubmitBottons(title, onFormReset, action)}
              </Spin>
            </Form>
          </fieldset>
        )}
      </div>
    </div>
  );
};

ServiceOfferingForm.propTypes = {
  action: PropTypes.string.isRequired,
  onActionClick: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSubmit: PropTypes.func,
  parentBread: PropTypes.array,

  serviceApi: PropTypes.object,
  serviceOfferingApi: PropTypes.object,
  serviceInstanceApi: PropTypes.object,
};
const mapStateToProps = (state) => ({
  serviceApi: state.apis.serviceApi,
  serviceInstanceApi: state.apis.serviceInstanceApi,
  serviceOfferingApi: state.apis.serviceOfferingApi,
});

const connectedServiceOfferingForm =
  connect(mapStateToProps)(ServiceOfferingForm);

export default withRouter(connectedServiceOfferingForm);
